import React, { useState } from 'react';
import { useSearchParams, useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

interface Props {
  variant?: 'staff' | 'member';
}

const ResetPassword: React.FC<Props> = ({ variant = 'staff' }) => {
  const { token: pathToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const queryToken = searchParams.get('token');
  const token = pathToken || queryToken || '';
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const endpoint =
    variant === 'member'
      ? '/api/member-auth/reset-password-with-token'
      : '/api/auth/reset-password-with-token';
  const loginPath = variant === 'member' ? '/member-login' : '/login';

  const missingToken = !token || token.length < 32;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND}${endpoint}`, { token, new_password: newPassword });
      toast.success(res.data?.message || 'Şifreniz güncellendi');
      setTimeout(() => navigate(loginPath), 600);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6b0f1a] to-[#8a1422] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-[#6b0f1a]">Yeni Şifre Belirleyin</h1>
            <p className="text-gray-600 text-sm mt-2">
              {variant === 'member'
                ? 'Üye portalı için yeni şifrenizi oluşturun.'
                : 'Hesabınız için yeni şifrenizi oluşturun.'}
            </p>
          </div>

          {missingToken ? (
            <div className="text-center space-y-3" data-testid="reset-missing-token">
              <p className="text-red-600 text-sm">Bu bağlantı geçersiz. Lütfen yeni bir şifre sıfırlama isteyin.</p>
              <Link to={variant === 'member' ? '/member-login' : '/forgot-password'} className="inline-block text-[#6b0f1a] text-sm hover:underline">
                ← Şifre sıfırlama sayfasına dön
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre (en az 6 karakter)</label>
                <input
                  type={show ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b0f1a]"
                  required
                  autoFocus
                  data-testid="reset-new-password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre (Tekrar)</label>
                <input
                  type={show ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={6}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#6b0f1a] ${confirmPassword && confirmPassword !== newPassword ? 'border-red-400' : 'border-gray-300'}`}
                  required
                  data-testid="reset-confirm-password"
                />
                {confirmPassword && confirmPassword !== newPassword && (
                  <p className="text-xs text-red-500 mt-1">Şifreler eşleşmiyor</p>
                )}
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
                Şifreleri göster
              </label>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#6b0f1a] text-white py-3 rounded-lg font-semibold hover:bg-[#8a1422] disabled:opacity-50"
                data-testid="reset-submit"
              >
                {submitting ? 'Kaydediliyor...' : 'Şifreyi Güncelle'}
              </button>
              <div className="text-center text-xs text-gray-500 pt-2">
                Bağlantının süresi 15 dakikadır. Süresi dolmuşsa yeni bir bağlantı isteyin.
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
